import { useCallback, useEffect, useRef, useState } from "react";

import { getSocket } from "./socket";
import { useAudioStore, effectiveVoiceVolume } from "../state/useAudioStore";
import { usePlayerStore } from "../state/usePlayerStore";
import { usePresenceStore } from "../state/usePresenceStore";

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

/**
 * Ab welcher Entfernung (in Welt-Einheiten / Pixel) wird der Voice-Chat
 * leiser? Innerhalb des Near-Radius ist die Stimme voll, ab Far-Radius
 * stumm; dazwischen wird linear interpoliert.
 */
const NEAR_RADIUS = 250;
const FAR_RADIUS = 1300;

function proximityFactor(dist: number): number {
  if (dist <= NEAR_RADIUS) return 1;
  if (dist >= FAR_RADIUS) return 0;
  return 1 - (dist - NEAR_RADIUS) / (FAR_RADIUS - NEAR_RADIUS);
}

type AnalyserBuffer = Uint8Array<ArrayBuffer>;

interface PeerEntry {
  pc: RTCPeerConnection;
  stream: MediaStream | null;
  /**
   * Auf iOS Safari nutzen wir bewusst ein `<video>`-Element für die
   * Audio-Wiedergabe. Sobald `getUserMedia` läuft, schaltet Safari nämlich
   * `<audio>`-Elemente in den „Anrufmodus" um und routet sie auf die
   * Hörmuschel – Spiel-SFX bleiben dann leise. `<video>` wird stattdessen
   * als „Medien-Modus" behandelt und kommt aus dem Hauptlautsprecher.
   */
  mediaEl: HTMLMediaElement | null;
  speaking: boolean;
  analyser?: AnalyserNode;
  analyserData?: AnalyserBuffer;
  /** Geglättete Lautstärke (0..1), inkl. Distanz und User-Volume */
  currentVolume: number;
  /** Aktueller Distanz-Faktor (0..1) – für UI-Indikator */
  proximity: number;
}

function createAnalyserBuffer(size: number): AnalyserBuffer {
  return new Uint8Array(new ArrayBuffer(size)) as AnalyserBuffer;
}

export interface VoicePeerView {
  id: string;
  speaking: boolean;
  hasStream: boolean;
  /** Anteil der maximalen Lautstärke, der durch die Distanz übrig bleibt (0..1) */
  proximity: number;
}

export interface VoiceChatHandle {
  enabled: boolean;
  muted: boolean;
  peers: VoicePeerView[];
  error: string | null;
  /** Wir selber – Pegel-Indikator für eigenes Mikro */
  speaking: boolean;
  /** Ist die Mikro-API überhaupt verfügbar (HTTPS / localhost)? */
  available: boolean;
  enable: () => Promise<void>;
  disable: () => void;
  toggleMute: () => void;
}

/**
 * Mesh-Voice-Chat: jeder Spieler hält pro Mitspieler eine RTCPeerConnection.
 * Signaling läuft über Socket.IO (`voice:*`-Events).
 *
 * Für die Offer/Answer-Verhandlung gilt: der Peer mit der lexikographisch
 * kleineren Player-ID ist der Initiator – so vermeiden wir Glare.
 */
export function useVoiceChat(): VoiceChatHandle {
  const myId = usePlayerStore((s) => s.id);
  const voiceVolume = useAudioStore((s) =>
    s.voiceMuted ? 0 : s.voiceVolume,
  );

  const [enabled, setEnabled] = useState(false);
  const [muted, setMuted] = useState(false);
  const [peers, setPeers] = useState<VoicePeerView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [speaking, setSpeaking] = useState(false);

  const peersRef = useRef<Map<string, PeerEntry>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const localAnalyserRef = useRef<AnalyserNode | null>(null);
  const localAnalyserDataRef = useRef<AnalyserBuffer | null>(null);
  const rafRef = useRef<number | null>(null);
  const myIdRef = useRef<string | null>(myId);
  myIdRef.current = myId;

  // ---------- Peer Helpers ----------

  const updatePeerView = useCallback(() => {
    const view: VoicePeerView[] = [];
    for (const [id, entry] of peersRef.current) {
      view.push({
        id,
        speaking: entry.speaking,
        hasStream: entry.stream !== null,
        proximity: entry.proximity,
      });
    }
    setPeers(view);
  }, []);

  const ensurePeer = useCallback(
    (peerId: string): PeerEntry => {
      let entry = peersRef.current.get(peerId);
      if (entry) return entry;

      const pc = new RTCPeerConnection(RTC_CONFIG);

      // Lokalen Audio-Track an die Verbindung hängen, falls schon vorhanden
      const localStream = localStreamRef.current;
      if (localStream) {
        for (const track of localStream.getTracks()) {
          pc.addTrack(track, localStream);
        }
      }

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          getSocket().emit("voice:signal", {
            to: peerId,
            signal: { kind: "ice", candidate: event.candidate.toJSON() },
          });
        }
      };

      pc.ontrack = (event) => {
        const stream = event.streams[0] ?? new MediaStream([event.track]);
        const existing = peersRef.current.get(peerId);
        if (!existing) return;
        existing.stream = stream;

        // iOS-Workaround: `<video>` statt `<audio>`. Sobald das Mikrofon
        // läuft, würde Safari ein `<audio>`-Element auf die Hörmuschel
        // routen – dann sind sowohl Voice als auch die Spiel-SFX leise.
        // `<video>` (auch ohne Bild) gilt als "Medien-Wiedergabe" und
        // landet im Hauptlautsprecher.
        const media = document.createElement("video");
        media.autoplay = true;
        media.playsInline = true;
        // Wir wollen die Videospur nicht sehen – Mikrofon liefert sowieso
        // keine. Position außerhalb des sichtbaren Bereichs, damit es nichts
        // im Layout reserviert.
        media.style.position = "fixed";
        media.style.width = "1px";
        media.style.height = "1px";
        media.style.opacity = "0";
        media.style.pointerEvents = "none";
        media.style.left = "-9999px";
        media.style.top = "0";
        media.muted = false;
        document.body.appendChild(media);
        media.srcObject = stream;
        media.volume = effectiveVoiceVolume(useAudioStore.getState());
        existing.currentVolume = media.volume;
        void media.play().catch(() => {
          /* Autoplay-Restriction – wird beim nächsten Klick freigeschaltet */
        });
        existing.mediaEl = media;

        // Pegel-Analyse für Speaking-Indikator
        const ctx = ensureAudioContext();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        existing.analyser = analyser;
        existing.analyserData = createAnalyserBuffer(analyser.frequencyBinCount);

        updatePeerView();
      };

      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === "failed" ||
          pc.connectionState === "closed" ||
          pc.connectionState === "disconnected"
        ) {
          removePeer(peerId);
        }
      };

      entry = {
        pc,
        stream: null,
        mediaEl: null,
        speaking: false,
        currentVolume: 0,
        proximity: 1,
      };
      peersRef.current.set(peerId, entry);
      updatePeerView();
      return entry;
    },
    [updatePeerView],
  );

  const removePeer = useCallback(
    (peerId: string) => {
      const entry = peersRef.current.get(peerId);
      if (!entry) return;
      try {
        entry.pc.close();
      } catch {
        // ignore
      }
      if (entry.mediaEl) {
        entry.mediaEl.pause();
        entry.mediaEl.srcObject = null;
        entry.mediaEl.remove();
      }
      peersRef.current.delete(peerId);
      updatePeerView();
    },
    [updatePeerView],
  );

  const handlePeerJoined = useCallback(
    async ({ playerId }: { playerId: string }) => {
      if (!myIdRef.current || playerId === myIdRef.current) return;
      const entry = ensurePeer(playerId);

      // Deterministischer Initiator: kleinere ID startet
      if (myIdRef.current < playerId) {
        try {
          const offer = await entry.pc.createOffer({
            offerToReceiveAudio: true,
          });
          await entry.pc.setLocalDescription(offer);
          getSocket().emit("voice:signal", {
            to: playerId,
            signal: { kind: "offer", sdp: offer.sdp ?? "" },
          });
        } catch (err) {
          console.warn("[voice] Offer fehlgeschlagen", err);
        }
      }
    },
    [ensurePeer],
  );

  const handlePeerLeft = useCallback(
    ({ playerId }: { playerId: string }) => {
      removePeer(playerId);
    },
    [removePeer],
  );

  const handleSignal = useCallback(
    async ({
      from,
      signal,
    }: {
      from: string;
      signal:
        | { kind: "offer"; sdp: string }
        | { kind: "answer"; sdp: string }
        | { kind: "ice"; candidate: RTCIceCandidateInit };
    }) => {
      const entry = ensurePeer(from);
      try {
        if (signal.kind === "offer") {
          await entry.pc.setRemoteDescription({ type: "offer", sdp: signal.sdp });
          const answer = await entry.pc.createAnswer();
          await entry.pc.setLocalDescription(answer);
          getSocket().emit("voice:signal", {
            to: from,
            signal: { kind: "answer", sdp: answer.sdp ?? "" },
          });
        } else if (signal.kind === "answer") {
          await entry.pc.setRemoteDescription({ type: "answer", sdp: signal.sdp });
        } else if (signal.kind === "ice") {
          if (signal.candidate) {
            await entry.pc.addIceCandidate(signal.candidate);
          }
        }
      } catch (err) {
        console.warn("[voice] Signaling-Fehler", err);
      }
    },
    [ensurePeer],
  );

  // ---------- Enable / Disable ----------

  const enable = useCallback(async () => {
    if (enabled) return;
    try {
      // Sicherheits-Check: Auf HTTP-Seiten (außer localhost) gibt's gar kein
      // `navigator.mediaDevices`. Wir geben dem User dann eine konkrete
      // Anleitung statt eines kryptischen Fehlers.
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices ||
        typeof navigator.mediaDevices.getUserMedia !== "function"
      ) {
        const insecure =
          typeof window !== "undefined" &&
          window.location.protocol !== "https:" &&
          window.location.hostname !== "localhost" &&
          window.location.hostname !== "127.0.0.1";
        setError(
          insecure
            ? "Voice-Chat braucht eine sichere Verbindung. Bitte über HTTPS oder direkt auf dem Laptop (localhost) öffnen."
            : "Dein Browser unterstützt das Mikrofon nicht.",
        );
        setEnabled(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      localStreamRef.current = stream;

      // Lokalen Pegel überwachen
      const ctx = ensureAudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      localAnalyserRef.current = analyser;
      localAnalyserDataRef.current = createAnalyserBuffer(analyser.frequencyBinCount);

      // Tracks an bereits bestehende Peers anhängen (sollte normalerweise leer sein)
      for (const entry of peersRef.current.values()) {
        for (const track of stream.getTracks()) {
          entry.pc.addTrack(track, stream);
        }
      }

      setError(null);
      setEnabled(true);

      const socket = getSocket();
      socket.emit("voice:join");
    } catch (err) {
      let message = "Mikro konnte nicht gestartet werden.";
      if (err instanceof DOMException) {
        if (err.name === "NotAllowedError" || err.name === "SecurityError") {
          message =
            "Mikro-Zugriff verweigert. Bitte in den Browser-Einstellungen erlauben und neu laden.";
        } else if (err.name === "NotFoundError") {
          message = "Kein Mikrofon gefunden.";
        } else if (err.name === "NotReadableError") {
          message = "Mikro wird gerade von einer anderen App belegt.";
        } else if (err.message) {
          message = err.message;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }
      setError(message);
      setEnabled(false);
    }
  }, [enabled]);

  const disable = useCallback(() => {
    if (!enabled) return;
    const socket = getSocket();
    socket.emit("voice:leave");

    for (const peerId of [...peersRef.current.keys()]) {
      removePeer(peerId);
    }

    if (localStreamRef.current) {
      for (const track of localStreamRef.current.getTracks()) {
        track.stop();
      }
      localStreamRef.current = null;
    }
    localAnalyserRef.current = null;
    localAnalyserDataRef.current = null;
    setEnabled(false);
    setMuted(false);
    setSpeaking(false);
  }, [enabled, removePeer]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    for (const track of stream.getAudioTracks()) {
      track.enabled = !next;
    }
    setMuted(next);
  }, [muted]);

  // (Lautstärke wird in der rAF-Schleife laufend aktualisiert – User-Slider
  // multipliziert mit Proximity-Faktor.)
  void voiceVolume;

  // ---------- Socket-Events bei aktivem Chat ----------

  useEffect(() => {
    if (!enabled) return undefined;
    const socket = getSocket();
    socket.on("voice:peer-joined", handlePeerJoined);
    socket.on("voice:peer-left", handlePeerLeft);
    socket.on("voice:signal", handleSignal);
    return () => {
      socket.off("voice:peer-joined", handlePeerJoined);
      socket.off("voice:peer-left", handlePeerLeft);
      socket.off("voice:signal", handleSignal);
    };
  }, [enabled, handlePeerJoined, handlePeerLeft, handleSignal]);

  // ---------- Pegel-Loop für Speaking-Indikatoren ----------

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      let viewDirty = false;

      const baseVol = effectiveVoiceVolume(useAudioStore.getState());
      const presence = usePresenceStore.getState().positions;
      const myPos = myIdRef.current ? presence[myIdRef.current] : undefined;

      // Mitspieler: Sprecher + Proximity berechnen, Lautstärke aktualisieren
      for (const [peerId, entry] of peersRef.current) {
        // Sprecher-Detection
        if (entry.analyser && entry.analyserData) {
          entry.analyser.getByteFrequencyData(entry.analyserData);
          const avg = average(entry.analyserData);
          const next = avg > 16;
          if (next !== entry.speaking) {
            entry.speaking = next;
            viewDirty = true;
          }
        }

        // Proximity-Faktor – wenn wir noch keine Positionen kennen,
        // bleibt die Stimme voll, damit niemand vor dem Spielstart in
        // einer Stille hängt.
        let prox = 1;
        const peerPos = presence[peerId];
        if (myPos && peerPos) {
          const dx = myPos.x - peerPos.x;
          const dy = myPos.y - peerPos.y;
          prox = proximityFactor(Math.hypot(dx, dy));
        }
        // Geglättete Anpassung an den Zielwert
        const target = baseVol * prox;
        const next = entry.currentVolume + (target - entry.currentVolume) * 0.18;
        entry.currentVolume = next;
        if (entry.mediaEl) {
          entry.mediaEl.volume = Math.max(0, Math.min(1, next));
        }
        // Proximity nur dann ins View aufnehmen, wenn die Änderung
        // groß genug ist, damit wir nicht jeden Frame re-rendern.
        if (Math.abs(entry.proximity - prox) > 0.05) {
          entry.proximity = prox;
          viewDirty = true;
        }
      }
      if (viewDirty) updatePeerView();

      // Selbst
      if (localAnalyserRef.current && localAnalyserDataRef.current) {
        localAnalyserRef.current.getByteFrequencyData(localAnalyserDataRef.current);
        const avg = average(localAnalyserDataRef.current);
        const nextSpeaking = !muted && avg > 16;
        setSpeaking((prev) => (prev === nextSpeaking ? prev : nextSpeaking));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, muted, updatePeerView]);

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      disable();
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function ensureAudioContext(): AudioContext {
    if (!audioContextRef.current) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      audioContextRef.current = new Ctor();
    }
    const ctx = audioContextRef.current;
    if (ctx.state === "suspended") {
      void ctx.resume().catch(() => {
        /* Browser blockt – wird beim nächsten Klick automatisch erlaubt */
      });
    }
    return ctx;
  }

  const available =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function";

  return {
    enabled,
    muted,
    peers,
    error,
    speaking,
    available,
    enable,
    disable,
    toggleMute,
  };
}

function average(arr: Uint8Array): number {
  let sum = 0;
  for (let i = 0; i < arr.length; i += 1) sum += arr[i] ?? 0;
  return sum / Math.max(1, arr.length);
}
